import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Skeleton,
  Table,
  TableContainer,
  Tag,
  TagRightIcon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react'
import { ReactElement } from 'react'
import { TbChevronLeft, TbChevronRight, TbPlus } from 'react-icons/tb'
import { useNavigate } from 'react-router-dom'
import useMobile from '../../hooks/useMobile'
import { getMapsUrl } from '../../pages/user/PlayExpand'
import { CopyLink, InfoCircle } from '../icons/icons'
import LazyAvatar from '../lazy/LazyAvatar'
import { LazyLoadedImage } from '../lazy/LazyLoadedImage'
import handleToast from '../toast/handleToast'

interface User {
  avatar: string
  id: string
  name: string
}
interface Event {
  type: 'event'
  id: string
  name: string
  thumbnail: string
  location: string
  duration: string
  price: number
}

export const DetailsPopover = ({
  thumbnail,
  name,
  location,
  duration,
  price,
  hostVersion,
}: {
  thumbnail: string
  name: string
  location: string
  duration: string
  price: number
  hostVersion?: boolean
}) => {
  const isMobile = useMobile(600)
  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button variant="unstyled">
          <Flex alignItems="center" justifyContent="space-between" gap="10px" cursor="pointer" w="100%">
            {/* <LazyBox
                width={hostVersion ? 53 : 40}
                height={hostVersion ? 58 : 40}
                borderRadius="4px"
                src={thumbnail}
                objectFit="cover"
              /> */}
            <LazyLoadedImage
              src={thumbnail}
              objectFit="cover"
              borderRadius="4px"
              flexShrink={0}
              width={hostVersion ? '53px' : '40px'}
              height={hostVersion ? '58px' : '40px'}
            />
            <Text
              variant="title"
              fontSize={hostVersion ? 'lg' : 'md'}
              flexGrow={1}
              noOfLines={1}
              title={name}
              textAlign="left"
            >
              {name}
            </Text>
            <Icon w="16px" h="16px" viewBox="0 0 50 54" color="#7E8B9A">
              <InfoCircle />
            </Icon>
          </Flex>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w={isMobile ? '350px' : '400px'}
          boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14;"
          borderRadius="10px"
          border="none"
        >
          <PopoverCloseButton />
          <PopoverHeader p="20px" border="none">
            <Text variant="title" fontSize="lg">
              Event Details
            </Text>
          </PopoverHeader>
          <PopoverBody p="0 20px 20px 20px">
            <Text variant="title" fontSize="md">
              Location
            </Text>
            <Text
              variant="subtle"
              fontSize="md"
              noOfLines={1}
              mb="10px"
              cursor="pointer"
              textDecoration="underline"
              onClick={() => window.open(getMapsUrl(location))}
            >
              {location}
            </Text>

            <Text variant="title" fontSize="md">
              Duration
            </Text>
            <Text variant="subtle" fontSize="md" noOfLines={1} mb="10px">
              {duration}
            </Text>

            <Text variant="title" fontSize="md">
              Price
            </Text>
            <Text variant="subtle" fontSize="md" noOfLines={1} mb="10px">
              ${price}
            </Text>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export type TrProps = (
  | { users: User[]; type: 'users'; allowAdd: boolean; onClick?: () => void }
  | Event
  | { label: string; type: 'string' }
  | { element: ReactElement; type: 'element' }
  | { label: string; type: 'copy-tag'; copyValue: string }
  | { label: string; type: 'copy-text' }
)[][]

interface DatagridProps {
  data: TrProps
  headers: string[]
  dateRange: [Date, Date] | null

  page: number
  pages: number
  onPageChange: (page: number) => void
  isLoading: boolean
  isSlim?: boolean
}

const Datagrid = (props: DatagridProps) => {
  const navigate = useNavigate()
  const toast = useToast()

  const onPreviousPage = () => {
    if (props.page > 1) {
      props.onPageChange(props.page - 1)
    }
  }
  const onNextPage = () => {
    if (props.page < props.pages) {
      props.onPageChange(props.page + 1)
    }
  }

  return (
    <Box
      w="100%"
      boxShadow={'0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14'}
      border="1px solid #DCE2EA"
      borderRadius="10px"
    >
      <TableContainer>
        <Table variant="simple" size={props.isSlim ? 'sm' : 'md'}>
          <Thead>
            <Tr borderColor="#DCE2EA" h={props.isSlim ? '40px' : '60px'}>
              {props.headers.map((header, index) => (
                <Th
                  textTransform="none"
                  color="#7E8B9A"
                  fontSize={props.isSlim ? 'sm' : 'md'}
                  fontWeight="500"
                  key={`header-${index}`}
                >
                  {header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {props.isLoading &&
              [1, 1, 1, 1, 1, 1, 1, 1, 1, 1].map((_, i: number) => {
                return (
                  <Tr key={i}>
                    {props.headers.map((_, j: number) => {
                      return (
                        <Td key={j}>
                          <Skeleton
                            h={props.isSlim ? '20px' : '40px'}
                            my={props.isSlim ? '10px' : undefined}
                            w="100%"
                          />
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })}
            {props.data.map((row, i) => {
              return (
                <Tr key={`row-${i}`} color="#7E8B9A">
                  {row.map((cell, j) => {
                    return (
                      <Td h="40px">
                        {cell.type === 'event' && (
                          <DetailsPopover
                            thumbnail={cell.thumbnail}
                            name={cell.name}
                            price={cell.price}
                            location={cell.location}
                            duration={cell.duration}
                          />
                        )}
                        {cell.type === 'string' && (
                          <Text variant="title" fontSize="sm">
                            {cell.label}
                          </Text>
                        )}
                        {cell.type === 'copy-tag' && (
                          <Tooltip label={cell.copyValue} hasArrow>
                            <Tag
                              variant="heavy"
                              cursor="pointer"
                              key={`time-${i}`}
                              onClick={() => {
                                handleToast(toast, 'UUID copied to clipboard!')
                                navigator.clipboard.writeText(cell.copyValue)
                              }}
                            >
                              {cell.label}
                              <TagRightIcon>
                                <Icon viewBox="0 0 20 20">
                                  <CopyLink color="white" />
                                </Icon>
                              </TagRightIcon>
                            </Tag>
                          </Tooltip>
                        )}
                        {cell.type === 'copy-text' && (
                          <Tooltip label={cell.label} hasArrow placement="bottom-start">
                            <Text
                              cursor={'pointer'}
                              onClick={() => {
                                handleToast(toast, 'Comment copied to clipboard!')
                                navigator.clipboard.writeText(cell.label)
                              }}
                              variant="title"
                              fontSize="sm"
                              maxW="300px"
                              overflow="hidden"
                              noOfLines={1}
                              title={cell.label}
                            >
                              {cell.label}
                            </Text>
                          </Tooltip>
                        )}
                        {cell.type === 'element' && cell.element}
                        {cell.type === 'users' && (
                          <AvatarGroup onClick={() => (cell.onClick ? cell.onClick() : {})}>
                            {cell.allowAdd && (
                              <Avatar
                                w="26px"
                                h="26px"
                                icon={<Icon as={TbPlus} color="#171C22" w="13px" h="13px" />}
                                border="1px solid #DCE2EA"
                                bgColor="#F5F7F9"
                                cursor="pointer"
                              />
                            )}
                            {cell.users.map((user, i) => {
                              return (
                                <LazyAvatar
                                  cursor="pointer"
                                  width={26}
                                  height={26}
                                  onClick={() => {
                                    if (cell.onClick == null) navigate(`/user/profile?id=${user.id}`)
                                  }}
                                  src={user.avatar}
                                  key={`${j}-${i}`}
                                  border="1px solid #DCE2EA"
                                />
                              )
                            })}
                          </AvatarGroup>
                        )}
                      </Td>
                    )
                  })}
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
      <Flex justifyContent="space-between">
        <Box />
        <Flex gap="12px" p="20px" alignItems="center">
          <IconButton
            variant="outline"
            aria-label="previous month"
            icon={<Icon w="18px" h="18px" as={TbChevronLeft} />}
            onClick={(e) => {
              e.preventDefault()
              onPreviousPage()
            }}
            isDisabled={props.page === 1}
          />
          <Button variant="heavy" borderRadius="6px" maxW="40px" cursor="default" _hover={{}} _active={{}}>
            {props.page}
          </Button>
          <Text variant="title" fontSize="md" m="8px">
            Of
          </Text>
          <Button variant="light" borderRadius="6px" maxW="40px" cursor="default" _hover={{}} _active={{}}>
            {props.pages}
          </Button>
          <IconButton
            variant="outline"
            aria-label="next month"
            icon={<Icon w="18px" h="18px" as={TbChevronRight} />}
            onClick={(e) => {
              e.preventDefault()
              onNextPage()
            }}
            isDisabled={props.page === props.pages}
          />
        </Flex>
      </Flex>
    </Box>
  )
}

export default Datagrid
